/**
 * Time Constants - Single Source of Truth
 * All time-related constants for the sd-tide application
 *
 * SYNC: This module is shared between frontend and backend.
 * Frontend imports via js/time.js, backend via netlify/functions/shared/time.mjs
 */

// ===========================================
// Data Freshness Thresholds (milliseconds)
// ===========================================

/**
 * How fresh data should be considered "live" vs "stale" vs "expired"
 * These define when UI shows warnings and when data should be refetched
 *
 * Source: netlify/functions/ingest/validators/temporal.mjs
 */
export const FRESHNESS = {
    buoy: {
        fresh: 30 * 60 * 1000,        // 30 minutes - NDBC updates ~hourly
        stale: 2 * 60 * 60 * 1000,    // 2 hours
        expired: 6 * 60 * 60 * 1000,  // 6 hours
    },
    tide: {
        fresh: 15 * 60 * 1000,        // 15 minutes - NOAA updates frequently
        stale: 60 * 60 * 1000,        // 1 hour
        expired: 3 * 60 * 60 * 1000,  // 3 hours
    },
    weather: {
        fresh: 60 * 60 * 1000,        // 1 hour - NWS updates hourly
        stale: 3 * 60 * 60 * 1000,    // 3 hours
        expired: 6 * 60 * 60 * 1000,  // 6 hours
    },
    sun: {
        fresh: 24 * 60 * 60 * 1000,   // 24 hours - daily data
        stale: 48 * 60 * 60 * 1000,   // 48 hours
        expired: 72 * 60 * 60 * 1000, // 72 hours
    },
};

// ===========================================
// Cache TTL (milliseconds)
// ===========================================

/**
 * How long to cache data locally before refetching
 * Shorter than freshness thresholds to ensure fresh data
 *
 * Source: js/config.js CACHE_CONFIG
 */
export const CACHE_TTL = {
    conditions: 5 * 60 * 1000,        // 5 minutes - matches poll-conditions
    tideCurve: 60 * 60 * 1000,        // 1 hour
    tidePredictions: 60 * 60 * 1000,  // 1 hour
    weather: 30 * 60 * 1000,          // 30 minutes
    buoy: 5 * 60 * 1000,              // 5 minutes
    waterLevel: 5 * 60 * 1000,        // 5 minutes
    sunTimes: 24 * 60 * 60 * 1000,    // 24 hours
};

// ===========================================
// Display Freshness Thresholds (for UI)
// ===========================================

/**
 * Thresholds for display age indicators (CSS classes)
 * Used by getDisplayAge() to return appropriate text and class
 *
 * Source: js/cache.js getDataAge(), js/ui.js updateFreshnessFromConditions()
 */
export const DISPLAY_AGE = {
    fresh: 5 * 60 * 1000,     // < 5 minutes = "fresh" class
    stale: 15 * 60 * 1000,    // 5-15 minutes = "stale" class
    // > 15 minutes = "old" class
};

// ===========================================
// Timeouts (milliseconds)
// ===========================================

/**
 * Network request timeouts
 *
 * Source: js/config.js THRESHOLDS.fetch, netlify/functions/ingest/fetchers/base.mjs
 */
export const TIMEOUTS = {
    fetchDirect: 10000,   // 10 seconds - direct API calls
    fetchProxy: 15000,    // 15 seconds - CORS proxy calls
    fetchText: 3000,      // 3 seconds - text endpoint calls
    fetchBackend: 5000,   // 5 seconds - backend fetcher timeout (fits within 30s function timeout)
};

// ===========================================
// Intervals (milliseconds)
// ===========================================

/**
 * Polling and retry intervals
 *
 * Source: js/config.js, netlify/functions/ingest/fetchers/base.mjs
 */
export const INTERVALS = {
    refresh: 5 * 60 * 1000,       // 5 minutes - main data refresh cycle
    retryBackoff: 500,            // 0.5 seconds - retry delay base
    circuitReset: 30 * 1000,      // 30 seconds - circuit breaker reset timeout
};

// ===========================================
// Rate Limiting
// ===========================================

/**
 * Rate limit configuration
 *
 * Source: netlify/functions/shared/rate-limit.mjs
 */
export const RATE_LIMIT = {
    windowSec: 3600,                     // 1 hour - rate limit window
    cleanupInterval: 10 * 60 * 1000,     // 10 minutes - fallback cleanup interval
};

// ===========================================
// Temporal Validation
// ===========================================

/**
 * Validation thresholds for data integrity
 *
 * Source: netlify/functions/ingest/validators/temporal.mjs
 */
export const VALIDATION = {
    futureThreshold: 5 * 60 * 1000,  // 5 minutes grace for clock drift
    stuckSensorReadings: 5,           // Number of identical readings = stuck sensor
};

// ===========================================
// Date Ranges (milliseconds)
// ===========================================

/**
 * Standard date ranges for API queries
 *
 * Source: netlify/functions/ingest/fetchers/noaa.mjs, js/api.js
 */
export const DATE_RANGES = {
    historicalLookback: 12 * 60 * 60 * 1000,  // 12 hours back
    predictionLookahead: 2 * 24 * 60 * 60 * 1000,  // 2 days forward
    curveLookahead: 12 * 60 * 60 * 1000,      // 12 hours forward (for tide curve)
};

// ===========================================
// Monitoring
// ===========================================

/**
 * Canary/monitoring thresholds
 *
 * Source: netlify/functions/canary.mjs
 */
export const MONITORING = {
    staleThresholdMinutes: 30,   // Data older than this triggers alerts
    recentWindowMinutes: 30,     // Window for checking recent failures/validations
    heartbeatStaleMinutes: 15,   // pipeline_runs.ran_at older than this = pipeline down
    pipelineFailureRatio: 0.2,   // failed/total_spots above this = pipeline unhealthy
    stickyMaxAgeMinutes: 6 * 60, // 6h sticky-value cap (matches ingest STICKY_VALUE_MAX_AGE_MINUTES)
    maxReportedSpots: 20,        // Cap on spot slugs listed in a single canary alert
};

// ===========================================
// UI Timing
// ===========================================

/**
 * UI animation and interaction timing constants
 *
 * Source: js/share.js, js/ui-selectors.js, various UI components
 */
export const UI_TIMING = {
    toastDuration: 2000,          // Toast notification display time
    debounceDefault: 300,         // Default debounce delay for inputs
    animationFast: 150,           // Fast UI animations
    animationNormal: 300,         // Normal UI transitions
    focusDelay: 100,              // Delay before focusing elements
};

// ===========================================
// Default Timezone
// ===========================================

/**
 * Default timezone for display formatting
 * Individual spots may have their own timezone in the database
 *
 * Source: js/config.js
 */
export const DEFAULT_TIMEZONE = 'America/Los_Angeles';

// ===========================================
// Data Retention (days)
// ===========================================

/**
 * How long to keep debug/monitoring data before cleanup
 * Tuned for Supabase free tier (500 MB limit)
 *
 * validated_readings: ~295K rows/day × 359 bytes = ~100 MB/day
 * raw_ingest_log: Currently disabled, but cleanup prevents bloat
 *
 * Source: netlify/functions/poll-conditions.mjs
 */
export const RETENTION = {
    validatedReadings: 1,  // 1 day (~100 MB)
    rawIngestLog: 1,       // 1 day (currently disabled, but cleanup old data)
    emailEvents: 30,       // 30 days (low volume)
    adminAuditLog: 90,     // 90 days (compliance)
};
